/**
 * AsignarDesdeCatalogoModal
 * Modal para asignar áreas desde el catálogo maestro a un presupuesto
 * Muestra el catálogo completo con estructura jerárquica (áreas → partidas → recursos)
 * Permite seleccionar flujos completos para copiarlos al presupuesto
 */

import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { areaService } from '../services/catalogoService';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { toast } from 'react-toastify';
import '../styles/modals.css';
import './AsignarDesdeCatalogoModal.css';

import Loading from './Loading';
const AsignarDesdeCatalogoModal = ({
  isOpen,
  onClose,
  presupuestoVentaId,
  presupuestoCostoId,
  cuentaId,
  tipo = 'venta',
  onAssigned
}) => {
  const [areas, setAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState(new Set());
  const [expandedAreas, setExpandedAreas] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAreas();
    }
  }, [isOpen]);

  const loadAreas = async () => {
    setLoading(true);
    try {
      console.log('Loading areas from API...');
      const response = await areaService.getAll({
        status: 'activo'
      });
      console.log('Full API response:', response);
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      console.log('Loaded areas from API:', data);
      if (data.length > 0) {
        console.log('First area sample:', data[0]);
        console.log('First area fields - id:', data[0].id, 'nombre:', data[0].nombre, 'descripcion:', data[0].descripcion);
      }
      setAreas(data);
      setSelectedAreas(new Set());
      setExpandedAreas(new Set());
    } catch (error) {
      console.error('Error loading areas:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      toast.error(`Error al cargar las áreas del catálogo: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleAreaSelection = (areaId) => {
    const newSelected = new Set(selectedAreas);
    if (newSelected.has(areaId)) {
      newSelected.delete(areaId);
    } else {
      newSelected.add(areaId);
    }
    setSelectedAreas(newSelected);
  };

  const toggleAreaExpanded = (areaId) => {
    const newExpanded = new Set(expandedAreas);
    if (newExpanded.has(areaId)) {
      newExpanded.delete(areaId);
    } else {
      newExpanded.add(areaId);
    }
    setExpandedAreas(newExpanded);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const handleAssignAreas = async () => {
    if (selectedAreas.size === 0) {
      toast.warning('Selecciona al menos un área');
      return;
    }

    setAssigning(true);
    try {
      console.log('🔍 handleAssignAreas() called');
      console.log('   selectedAreas:', Array.from(selectedAreas));
      console.log('   areas.length:', areas.length);
      console.log('   areas array:', areas);

      if (areas.length > 0) {
        console.log('   First area keys:', Object.keys(areas[0]));
        console.log('   First area:', areas[0]);
      } else {
        console.log('   ❌ ERROR: areas array is empty!');
      }

      // Get the full area objects (not just IDs)
      const selectedAreaObjects = Array.from(selectedAreas).map(areaId => {
        console.log(`\n   Looking for area with id=${areaId} (type: ${typeof areaId})`);
        // Convert to number for safe comparison
        const numericId = parseInt(areaId, 10);
        console.log(`   Searching in ${areas.length} areas...`);
        const found = areas.find(a => {
          const aId = parseInt(a.id, 10);
          const matches = aId === numericId;
          if (matches) {
            console.log(`     ✅ MATCH: ${aId} === ${numericId}`);
          }
          return matches;
        });
        if (!found) {
          console.log(`   ❌ NOT FOUND: area with id=${areaId}`);
        } else {
          console.log(`   ✅ Found:`, found);
        }
        return found;
      }).filter(Boolean);

      console.log(`\nTotal areas found: ${selectedAreaObjects.length} (from ${selectedAreas.size} selected)`);
      console.log('Sending areas:', selectedAreaObjects);

      if (tipo === 'venta') {
        await presupuestoHierarchyService.assignCatalogAreasToVenta(
          presupuestoVentaId,
          selectedAreaObjects
        );
      } else {
        await presupuestoHierarchyService.assignCatalogAreasToCosto(
          presupuestoCostoId,
          selectedAreaObjects
        );
      }

      toast.success(`${selectedAreas.size} área(s) asignada(s) exitosamente`);
      setSelectedAreas(new Set());
      if (onAssigned) {
        onAssigned();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning areas:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar áreas');
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl agregar-area-presupuesto-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Asignar Áreas desde Catálogo</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <Loading fullScreen={false} size="sm" mensaje="Cargando áreas disponibles" />
            </div>
          ) : areas.length === 0 ? (
            <div className="empty-state">
              <p>No hay áreas disponibles en el catálogo</p>
              <p className="hint">Crea áreas en el catálogo maestro primero</p>
            </div>
          ) : (
            <div className="areas-selection">
              <div className="instructions">
                <p>Selecciona los flujos completos que deseas agregar al presupuesto.</p>
                <p className="hint">Cada área incluye todas sus partidas y recursos configurados.</p>
              </div>

              <div className="areas-list-hierarchy">
                {areas.map(area => (
                  <div key={area.id} className={`area-hierarchy-item ${selectedAreas.has(area.id) ? 'selected' : ''}`}>
                    {/* Area Header */}
                    <div className="area-header-row">
                      <div className="area-header-left">
                        <button
                          className="expand-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAreaExpanded(area.id);
                          }}
                        >
                          {expandedAreas.has(area.id) ? <FiChevronDown /> : <FiChevronRight />}
                        </button>

                        <input
                          type="checkbox"
                          checked={selectedAreas.has(area.id)}
                          onChange={() => toggleAreaSelection(area.id)}
                          className="area-checkbox"
                          onClick={(e) => e.stopPropagation()}
                        />

                        <div className="area-info" onClick={() => toggleAreaExpanded(area.id)}>
                          <h4>{area.nombre}</h4>
                          {area.descripcion && (
                            <p className="description">{area.descripcion}</p>
                          )}
                        </div>
                      </div>

                      <div className="area-stats">
                        <span className="stat">
                          {(area.partidas || []).length} partida(s)
                        </span>
                        <span className="stat stat-cost">
                          Costo: {formatCurrency(area.valor_unitario_costo)}
                        </span>
                        <span className="stat stat-sale">
                          Venta: {formatCurrency(area.valor_unitario_venta)}
                        </span>
                        <span className="stat stat-margin">
                          Margen: {(parseFloat(area.markup_promedio) || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    {/* Partidas y Recursos (expandible) */}
                    {expandedAreas.has(area.id) && (
                      <div className="area-content">
                        {area.partidas && area.partidas.length > 0 ? (
                          <div className="partidas-list">
                            {area.partidas.map(partida => (
                              <div key={partida.id} className="partida-item">
                                <div className="partida-header">
                                  <div className="partida-name">
                                    <strong>📋 {partida.nombre}</strong>
                                    {partida.descripcion && (
                                      <span className="partida-desc">{partida.descripcion}</span>
                                    )}
                                  </div>
                                  <div className="partida-stats">
                                    <span className="mini-stat">
                                      {(partida.recursos || []).length} recurso(s)
                                    </span>
                                    <span className="mini-stat">
                                      Costo: {formatCurrency(partida.valor_unitario_costo)}
                                    </span>
                                    <span className="mini-stat">
                                      Venta: {formatCurrency(partida.valor_unitario_venta)}
                                    </span>
                                  </div>
                                </div>

                                {/* Recursos */}
                                {partida.recursos && partida.recursos.length > 0 && (
                                  <div className="recursos-list">
                                    {partida.recursos.map(recurso => (
                                      <div key={recurso.id} className="recurso-item">
                                        <div className="recurso-name">
                                          <span className="recurso-icon">•</span>
                                          {recurso.nombre}
                                        </div>
                                        <div className="recurso-details">
                                          <span>{recurso.cantidad_presupuesto} {recurso.unidad}</span>
                                          <span>×</span>
                                          <span>{formatCurrency(recurso.precio_unitario_venta)}</span>
                                          <span>=</span>
                                          <span className="recurso-subtotal">
                                            {formatCurrency(recurso.subtotal)}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="empty-partidas">
                            <p>Esta área no tiene partidas configuradas</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {selectedAreas.size > 0 && (
                <div className="selection-summary">
                  <strong>{selectedAreas.size} flujo(s) seleccionado(s)</strong>
                  <span className="summary-hint">
                    Se copiarán las áreas con todas sus partidas y recursos
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={assigning}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAssignAreas}
            disabled={selectedAreas.size === 0 || assigning || loading}
          >
            {assigning ? 'Asignando...' : (
              <>
                <FiPlus /> Asignar {selectedAreas.size > 0 ? `(${selectedAreas.size})` : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AsignarDesdeCatalogoModal;
